/**
 * Auto-response utility for common user acknowledgments and phrases
 * Provides instant responses for better chat flow and user experience
 * Also includes basic prompt injection detection as first line of defense
 */

/**
 * Pattern matching for common acknowledgment phrases
 */
const acknowledgmentPatterns = [
  // Thanks variations (safe - always standalone acknowledgments)
  /^thanks?!*$/i,
  /^thank you!*$/i,
  /^ty!*$/i,
  /^thx!*$/i,
  /^much appreciated!*$/i,
  /^appreciated!*$/i,
  /^thanks so much!*$/i,
  /^thank you so much!*$/i,
  
  // Greetings (safe - clearly conversation starters)
  /^hi!*$/i,
  /^hello!*$/i,
  /^hey!*$/i,
  /^hey there!*$/i,
  /^good morning!*$/i,
  /^good afternoon!*$/i,
  /^good evening!*$/i,
  /^what's up!*$/i,
  
  // Positive acknowledgments (safe - clearly reactions, not answers)
  /^got it!*$/i,
  /^understood!*$/i,
  /^makes sense!*$/i,
  /^perfect!*$/i,
  /^awesome!*$/i,
  /^great!*$/i,
  /^nice!*$/i,
  /^cool!*$/i,
  /^sweet!*$/i,
  /^excellent!*$/i,
  
  // NOTE: Removed confirmation patterns (ok, yes, sure, etc.) 
  // These are contextual and could be answers to agent questions
];

/**
 * Prompt injection detection patterns (basic first line of defense)
 */
const promptInjectionPatterns = [
  // Classic prompt injection attempts
  /ignore\s+previous\s+instructions/i,
  /forget\s+everything/i,
  /new\s+instructions?/i,
  /system\s*:?\s*you\s+(are|should)/i,
  /assistant\s*:?\s*i\s+(am|should)/i,
  /roleplay\s+as/i,
  /act\s+as\s+(?:if\s+)?you\s+(are|were)/i,
  /pretend\s+(?:to\s+be|you\s+are)/i,
  
  // Information extraction attempts
  /reveal\s+your\s+(prompt|instructions|system)/i,
  /what\s+(?:are\s+)?your\s+(instructions|prompt|system)/i,
  /show\s+me\s+your\s+(prompt|instructions|system)/i,
  /tell\s+me\s+your\s+(prompt|instructions|system)/i,
  
  // Role confusion attempts
  /you\s+are\s+now\s+a/i,
  /from\s+now\s+on\s+you\s+are/i,
  /override\s+your/i,
  /disable\s+your/i,
  
  // Command injection style
  /\$\{.*\}/,  // Template literal injection
  /`.*`/,      // Backtick commands (less strict)
  /<script/i,  // Script tags
  /javascript:/i, // JavaScript protocol
];

/**
 * Prompt injection response templates
 */
const promptInjectionResponses = [
  "I'm designed to help with SEO analysis and can't process that type of request. What SEO questions can I help you with instead? 🔍",
  "That looks like it might be trying to change how I work! I'm here to help with your SEO performance - what would you like to know? 📊",
  "I can only assist with SEO-related questions and analysis. What aspects of your site's performance would you like to explore? 🚀",
  "I'm focused on helping with SEO insights and can't handle that request. How about we look at your keywords or page performance instead? 📈"
];

/**
 * Auto-response templates with variations for natural feel
 */
const autoResponseTemplates = {
  thanks: [
    "You're welcome! Happy to help with your SEO. 😊",
    "My pleasure! Let me know if you need anything else.",
    "Anytime! I'm here whenever you need SEO guidance.",
    "Glad I could help! Feel free to ask more questions.",
  ],
  
  greeting: [
    "Hello! Ready to boost your SEO performance? 🚀",
    "Hi there! How can I help with your site today?",
    "Hey! What SEO challenges can I tackle for you?",
    "Good to see you! What would you like to explore?",
  ],
  
  positive: [
    "Excellent! Anything else I can help clarify?",
    "Great to hear! What's next on your SEO to-do list?",
    "Perfect! Ready for the next challenge?",
    "Awesome! Let me know how else I can assist.",
  ]
};

/**
 * Check for potential prompt injection attempts
 */
function detectPromptInjection(message) {
  const text = message.trim();
  
  // Check against known injection patterns
  for (const pattern of promptInjectionPatterns) {
    if (pattern.test(text)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Get a random prompt injection response
 */
function getPromptInjectionResponse() {
  const randomIndex = Math.floor(Math.random() * promptInjectionResponses.length);
  return promptInjectionResponses[randomIndex];
}

/**
 * Categorize the user message type
 */
function categorizeMessage(message) {
  const text = message.trim();
  
  // SECURITY: Check for prompt injection first (highest priority)
  if (detectPromptInjection(text)) {
    return 'prompt_injection';
  }
  
  // Check for thanks patterns (always safe to auto-respond)
  if (acknowledgmentPatterns.slice(0, 8).some(pattern => pattern.test(text))) {
    return 'thanks';
  }
  
  // Check for greeting patterns (always safe to auto-respond)  
  if (acknowledgmentPatterns.slice(8, 16).some(pattern => pattern.test(text))) {
    return 'greeting';
  }
  
  // Check for positive acknowledgment patterns (safe reactions, not answers)
  if (acknowledgmentPatterns.slice(16, 26).some(pattern => pattern.test(text))) {
    return 'positive';
  }
  
  // No confirmation patterns - these are contextual and could be answers
  
  return null;
}

/**
 * Get a random response from the appropriate category
 */
function getRandomResponse(category) {
  // Handle prompt injection specially
  if (category === 'prompt_injection') {
    return getPromptInjectionResponse();
  }
  
  const responses = autoResponseTemplates[category];
  if (!responses || responses.length === 0) return null;
  
  const randomIndex = Math.floor(Math.random() * responses.length);
  return responses[randomIndex];
}

/**
 * Log potential security incidents (for monitoring)
 */
function logSecurityIncident(message, detectedPattern) {
  // In production, this could send to an analytics service
  console.warn('🛡️ [Security] Potential prompt injection detected:', {
    message: message.substring(0, 100) + (message.length > 100 ? '...' : ''),
    detectedPattern: detectedPattern,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent
  });
}

/**
 * Check if a message should get an auto-response
 * Returns the response text if match found, null otherwise
 */
export function getAutoResponse(message) {
  if (!message || typeof message !== 'string') return null;
  
  const category = categorizeMessage(message);
  if (!category) return null;
  
  // Log security incidents for monitoring
  if (category === 'prompt_injection') {
    const detectedPattern = promptInjectionPatterns.find(pattern => pattern.test(message));
    logSecurityIncident(message, detectedPattern?.source || 'unknown');
  }
  
  return getRandomResponse(category);
}

/**
 * Check if a message is a simple acknowledgment that should get instant response
 */
export function isAutoResponseCandidate(message) {
  return getAutoResponse(message) !== null;
}

/**
 * Create an auto-response message object
 */
export function createAutoResponseMessage(originalMessage, messageId) {
  const response = getAutoResponse(originalMessage);
  if (!response) return null;
  
  return {
    id: messageId,
    type: 'agent',
    content: response,
    timestamp: new Date(),
    isAutoResponse: true,
    originalMessage: originalMessage.trim()
  };
}

/**
 * Direct export for prompt injection detection (for use in other components)
 */
export function detectPotentialInjection(message) {
  return detectPromptInjection(message);
}
